import {
  BlockTypeSelectItem,
  DefaultReactSuggestionItem,
  blockTypeSelectItems,
  getDefaultReactSlashMenuItems,
} from '@blocknote/react';

import { Option } from '@/shared/types/rules';

import { SharedEditorInstance } from './schema';

/**
 * Convert variable options to BlockNote suggestion items
 */
export const getVariableSuggestionItems = (
  editor: SharedEditorInstance,
  variableOptions: Option[],
  t: (key: string) => string
): DefaultReactSuggestionItem[] => {
  return variableOptions.map((option) => {
    const groupKey = option.type || 'available_variables';
    const groupLabel = t(`auto_complete.group_labels.${groupKey}`);
    const label = option.label || option.value || '';

    return {
      title: label,
      group: groupLabel,
      aliases: [option.label, option.value].filter(Boolean) as string[],
      onItemClick: () => {
        // Strip any existing braces from value (handles both bare $var and {{$var}} formats)
        const bareValue = (option.value || '').replace(/^\{\{/, '').replace(/\}\}$/, '');

        // Check if {{ precedes the cursor (user typed {{$ manually)
        const tiptap = editor._tiptapEditor;
        const { from } = tiptap.state.selection;
        const lookbackLength = Math.min(from, 3);
        const textBefore = tiptap.state.doc.textBetween(from - lookbackLength, from);
        const hasBracesBefore = textBefore.includes('{{');

        if (hasBracesBefore) {
          editor.insertInlineContent([`${bareValue}}} `]);
        } else {
          editor.insertInlineContent([`{{${bareValue}}} `]);
        }
      },
    };
  });
};

/**
 * Derive the slash menu key from a BlockTypeSelectItem based on its type and props.
 *
 * This mapping is coupled to BlockNote's internal block types and prop shapes.
 * If BlockNote adds new block types, they will fall through to '' and be excluded
 * from filtered lists — update this function when upgrading BlockNote.
 */
const getSlashKeyForBlockTypeItem = (item: BlockTypeSelectItem): string => {
  if (item.type === 'paragraph') {
    return 'paragraph';
  }
  if (item.type === 'quote') {
    return 'quote';
  }
  if (item.type === 'toggleListItem') {
    return 'toggle_list';
  }
  if (item.type === 'bulletListItem') {
    return 'bullet_list';
  }
  if (item.type === 'numberedListItem') {
    return 'numbered_list';
  }
  if (item.type === 'checkListItem') {
    return 'check_list';
  }
  if (item.type === 'heading') {
    const level = item.props?.level as number | undefined;
    const isToggleable = item.props?.isToggleable;
    if (isToggleable) {
      return level === 1 ? 'toggle_heading' : `toggle_heading_${level}`;
    }
    return level === 1 ? 'heading' : `heading_${level}`;
  }
  return '';
};

/**
 * Get block type select items for the formatting toolbar, filtered by allowed keys.
 */
export const getFilteredBlockTypeItems = (
  editor: SharedEditorInstance,
  allowedKeys?: string[]
): BlockTypeSelectItem[] | undefined => {
  if (!allowedKeys) {
    return undefined;
  }

  const allowedSet = new Set(allowedKeys);
  return blockTypeSelectItems(editor.dictionary).filter((item) =>
    allowedSet.has(getSlashKeyForBlockTypeItem(item))
  );
};

/**
 * Type guard for slash menu items that have a `key` property.
 * DefaultReactSuggestionItem omits `key` from its type, but the runtime objects
 * returned by getDefaultReactSlashMenuItems() inherit it from DefaultSuggestionItem.
 */
const hasKey = (
  item: DefaultReactSuggestionItem
): item is DefaultReactSuggestionItem & { key: string } =>
  typeof (item as Record<string, unknown>).key === 'string';

/**
 * Get slash menu items for the editor, optionally filtered by allowed keys.
 */
export const getSlashMenuItems = (
  editor: SharedEditorInstance,
  allowedKeys?: string[]
): DefaultReactSuggestionItem[] => {
  const allItems = getDefaultReactSlashMenuItems(editor);

  if (!allowedKeys) {
    return allItems.map((item) => ({ ...item, badge: undefined }));
  }

  const allowedSet = new Set(allowedKeys);
  return allItems
    .filter((item) => hasKey(item) && allowedSet.has(item.key))
    .map((item) => ({ ...item, badge: undefined }));
};
